/**
 * SaaS Hindi/English i18n — Stage B.6
 * Scope: Fleet + Maintenance admin UIs only (per spec).
 * Marketplace, CRM and public site stay English-only.
 * Persists selection to localStorage.
 */
import { createContext, useContext, useEffect, useState } from "react";

const DICT = {
  en: {
    // Fleet
    fleet_tenants: "Fleet Tenants (SaaS)",
    fleet_cloud: "Fleet Cloud",
    maintenance_cloud: "Maintenance Cloud",
    onboard_tenant: "Onboard tenant",
    add_asset: "Add asset",
    total_assets: "Total assets",
    utilization: "Utilization",
    idle: "Idle",
    readiness: "Readiness",
    available: "Available",
    deployed: "Deployed",
    breakdown: "Breakdown",
    retired: "Retired",
    ask_ai_fleet: "Ask AI Fleet Manager",
    save_asset: "Save asset",
    filter: "Filter",
    // Maintenance
    new_work_order: "New work order",
    total_wos: "Total WOs",
    open: "Open",
    in_progress: "In progress",
    breakdowns_open: "Breakdowns",
    health_score: "Health score",
    preventive: "Preventive",
    inspection: "Inspection",
    warranty: "Warranty",
    create_wo: "Create WO",
    // Common
    language: "Language",
    back: "Back",
    print: "Print",
    close: "Close",
    photos_docs: "Photos & documents",
    supplier_identity_protected: "Identity protected by EHI",
  },
  hi: {
    // Fleet
    fleet_tenants: "फ्लीट टेनेंट (SaaS)",
    fleet_cloud: "फ्लीट क्लाउड",
    maintenance_cloud: "रखरखाव क्लाउड",
    onboard_tenant: "टेनेंट जोड़ें",
    add_asset: "एसेट जोड़ें",
    total_assets: "कुल एसेट",
    utilization: "उपयोगिता",
    idle: "बेकार",
    readiness: "तैयारी",
    available: "उपलब्ध",
    deployed: "तैनात",
    breakdown: "खराब",
    retired: "सेवानिवृत्त",
    ask_ai_fleet: "AI फ्लीट मैनेजर से पूछें",
    save_asset: "एसेट सहेजें",
    filter: "फ़िल्टर",
    // Maintenance
    new_work_order: "नया वर्क ऑर्डर",
    total_wos: "कुल WO",
    open: "खुला",
    in_progress: "प्रगति में",
    breakdowns_open: "खराबी",
    health_score: "स्वास्थ्य स्कोर",
    preventive: "निवारक",
    inspection: "निरीक्षण",
    warranty: "वारंटी",
    create_wo: "WO बनाएँ",
    // Common
    language: "भाषा",
    back: "वापस",
    print: "प्रिंट",
    close: "बंद",
    photos_docs: "तस्वीरें और दस्तावेज़",
    supplier_identity_protected: "पहचान EHI द्वारा सुरक्षित",
  },
};

const I18nCtx = createContext({ lang: "en", t: (k) => k, setLang: () => {} });

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(() => localStorage.getItem("ehi_saas_lang") || "en");
  useEffect(() => { localStorage.setItem("ehi_saas_lang", lang); }, [lang]);
  const t = (key) => DICT[lang]?.[key] ?? DICT.en[key] ?? key;
  const setLang = (v) => setLangState(v === "hi" ? "hi" : "en");
  return <I18nCtx.Provider value={{ lang, t, setLang }}>{children}</I18nCtx.Provider>;
}

export function useT() {
  return useContext(I18nCtx);
}

export function LangToggle({ className = "" }) {
  const { lang, setLang } = useT();
  return (
    <div className={`inline-flex rounded-md border border-slate-300 overflow-hidden text-xs font-semibold ${className}`} data-testid="lang-toggle">
      {["en", "hi"].map((l) => (
        <button
          key={l}
          onClick={() => setLang(l)}
          className={`px-2.5 py-1 transition-colors ${lang === l ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
          data-testid={`lang-${l}`}
        >
          {l === "en" ? "EN" : "हिं"}
        </button>
      ))}
    </div>
  );
}
